import { Button, Stack } from '@mui/material';
import type { ChatError } from '@mui/x-chat/types';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link as ReactRouterLink } from 'react-router-dom';

import {
  Alert,
  Loader,
  api,
  getQueryProps,
  isDefined,
  isTruthy,
  useLoggedInAuthContext,
} from 'common';

import { createAgenticChatAdapter } from './Agent/adapter';
import { getReplayedChatMessages } from './Agent/replayMessages';
import { AgenticChatConversationChat } from './Conversation/Chat';
import { useAgenticChatContext } from '../../components/AgenticChat/context';

interface AgenticChatContentProps {
  sessionId?: string;
}

export function AgenticChatContent(props: AgenticChatContentProps) {
  const { sessionId } = props;
  const { agenticChatVisibility, closeAgenticChat } = useAgenticChatContext();
  const { refetch: refetchSessions } = useQuery({
    ...getQueryProps(api.client.AgenticPlatformSession, 'findAll', {
      filterMode: 'OnlySessionsWithMessages',
    }),
    enabled: false,
  });
  const { data: session, refetch: refetchSession } = useQuery({
    ...getQueryProps(api.client.AgenticPlatformSession, 'findOne', sessionId ?? ''),
    enabled: false,
  });
  const { isAdminRole, hasPermission } = useLoggedInAuthContext();
  const [agenticChatAdapter] = useState(() => createAgenticChatAdapter());
  const [runError, setRunError] = useState<ChatError>();
  const messagesResult = useQuery({
    ...getQueryProps(api.client.AgenticPlatformSession, 'findAllMessages', sessionId ?? ''),
    enabled: isDefined(sessionId),
    select: (messages) => getReplayedChatMessages(messages),
    retry: false,
    refetchOnMount: 'always',
    // Background refetches would remount the chat and discard drafts or interrupt runs.
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  if (isDefined(sessionId) && (messagesResult.isPending || messagesResult.isFetching)) {
    return (
      <Stack
        role="status"
        sx={{
          flex: 1,
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Loader label="Loading chat" />
      </Stack>
    );
  }

  if (messagesResult.isError) {
    if (
      agenticChatVisibility === 'Open' &&
      isDefined(sessionId) &&
      messagesResult.error instanceof api.Common.APIError &&
      messagesResult.error.status === 404 &&
      isAdminRole &&
      isTruthy(hasPermission.ViewAllChatSessions)
    ) {
      return (
        <Stack gap={2} sx={{ padding: 2 }}>
          <Alert error={messagesResult.error} />
          <Button
            component={ReactRouterLink}
            to={`/agentic-chat-logs/${sessionId}`}
            onClick={() => closeAgenticChat()}
          >
            View session history
          </Button>
        </Stack>
      );
    }
    return <Alert error={messagesResult.error} sx={{ margin: 2 }} />;
  }

  return (
    <Stack sx={{ flex: 1, minHeight: 0 }}>
      {isDefined(runError) ? (
        <Alert error={runError} sx={{ margin: 2, whiteSpace: 'pre-line' }} />
      ) : null}
      <AgenticChatConversationChat
        agenticChatVisibility={agenticChatVisibility}
        adapter={agenticChatAdapter}
        initialMessages={messagesResult.data ?? []}
        initialActiveConversationId={sessionId}
        onError={(chatError) => setRunError(chatError)}
        onFinish={(payload) => {
          void refetchSessions();
          // Only an untitled chat can gain a generated name, saved before its first run finishes.
          if (isDefined(sessionId) && !isDefined(session?.title)) {
            void refetchSession();
          }
          if (!payload.isError) {
            setRunError(undefined);
          }
        }}
      />
    </Stack>
  );
}
